/**
 * Asteroid field view tuning (view.json, Zod-validated): when to re-bucket LODs, the switch hysteresis and
 * the distance (per metre of rock radius) beyond which a rock is too small to draw, and the rock tint (MC-a6b).
 */
import { z } from 'zod'
import viewJson from './view.json'

export const fieldViewConfigSchema = z.object({
  /** Re-bucket when the camera has moved this far (m) since the last bucketing. */
  rebucketMeters: z.number().positive(),
  /** ...or after this many frames. */
  rebucketFrames: z.number().int().positive(),
  /** Relative band around each LOD threshold (0.1 = ±10%). */
  hysteresis: z.number().min(0).max(0.5),
  /**
   * Rocks farther than this × radius are not drawn (sub-pixel: at 3000 r a rock spans about 0.04° of view).
   * Their colliders stay. 0 = never cull.
   */
  cullDistancePerRadius: z.number().min(0),
  /**
   * The rock tint by grade and signature (MC-a6b, tint.ts): brightness per unit of grade multiplier above 1 and the
   * lean toward the signature ore's colour. Both 0 = neutral (no instance colour: today's look exactly).
   */
  tint: z.object({
    brightnessPerGrade: z.number().min(0).max(1),
    signatureHue: z.number().min(0).max(1),
  }),
  /**
   * PF-2 (perf pass): rocks cast shadows from one coarse instance set per archetype (the kit's coarsest LOD) on the
   * shadow-only layer instead of from the drawn buckets. `enabled` per preset (off = the buckets cast, the old path);
   * `padM` is the slack (m) added to the live shadow reach before a rock leaves the set; `shrink` scales each caster
   * about its centre (1 = none; a 0.97-0.99 trial value is for the gate's scene-B check only). `nearSet` (PF-2b) per
   * preset: rocks touching shadow level 1's box also cast at their visible LOD into levels 0-1 (`SHADOW_NEAR`), so close
   * rocks keep their crater-rim shadows (off = the coarse set only; needs `enabled`).
   */
  shadowCasters: z.object({
    enabled: z.object({ low: z.boolean(), medium: z.boolean(), high: z.boolean() }),
    nearSet: z.object({ low: z.boolean(), medium: z.boolean(), high: z.boolean() }),
    padM: z.number().min(0),
    shrink: z.number().min(0.9).max(1),
  }),
})

export type FieldViewConfig = z.infer<typeof fieldViewConfigSchema>

export function loadFieldViewConfig(): FieldViewConfig {
  return fieldViewConfigSchema.parse(viewJson)
}
